import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PssCharacterDesign, PssCrewMember } from '../../../services/pss-auth.service';
import { spriteIdOrEmpty } from '../pss-display-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-automation-modal',
  standalone: true,
  imports: [CommonModule, PssSpriteComponent],
  templateUrl: './automation-modal.component.html',
  styleUrls: ['../dashboard.component.css']
})
export class AutomationModalComponent {
  @Input() show = false;
  @Input() automationFeatureEnabled = false;
  @Input() automationError = '';
  @Input() autoTaskSaving = false;

  @Input() autoEnableBattleSync = false;
  @Input() autoEnableDonationExtend = false;
  @Input() autoDonationSelectedIds = new Set<string>();
  @Input() autoScheduleType: 'interval' | 'cron' | 'times' = 'cron';
  @Input() autoIntervalSec = 86400;
  @Input() autoCronExpression = '0 2 * * *';
  @Input() autoScheduleTimes: string[] = [];
  @Input() autoScheduleTimeInput = '02:00';

  @Input() donatedCrew: PssCrewMember[] = [];
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();

  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<void>();

  @Output() setAutoEnableBattleSync = new EventEmitter<boolean>();
  @Output() setAutoEnableDonationExtend = new EventEmitter<boolean>();
  @Output() toggleDonationSelection = new EventEmitter<{ characterId: string; selected: boolean }>();
  @Output() setAutoScheduleType = new EventEmitter<'interval' | 'cron' | 'times'>();
  @Output() setAutoIntervalSec = new EventEmitter<number>();
  @Output() setAutoCronExpression = new EventEmitter<string>();
  @Output() setAutoScheduleTimeInput = new EventEmitter<string>();

  @Output() addScheduleTime = new EventEmitter<void>();
  @Output() removeScheduleTime = new EventEmitter<string>();

  onAutoScheduleTypeChange(value: string): void {
    const scheduleType = value as 'interval' | 'cron' | 'times';
    this.setAutoScheduleType.emit(scheduleType);
  }

  onScheduleTimeInputChange(localValue: string): void {
    this.setAutoScheduleTimeInput.emit(this.localTimeToUtc(localValue));
  }

  onIntervalSecChange(value: string): void {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      this.setAutoIntervalSec.emit(parsed);
    }
  }

  crewSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.characterDesigns.get(designId)?.profileSpriteId);
  }

  crewRarity(designId: string): string {
    return this.characterDesigns.get(designId)?.rarity ?? '';
  }

  crewName(crew: PssCrewMember): string {
    return crew.customName || this.characterDesigns.get(crew.designId)?.name || `#${crew.characterId}`;
  }

  /** Display value for the time input — converts stored UTC time to local HH:MM. */
  get localScheduleTimeInput(): string {
    return this.utcTimeToLocal(this.autoScheduleTimeInput);
  }

  /** Convert "HH:MM" UTC string to local "HH:MM" string. */
  utcTimeToLocal(utcTime: string): string {
    if (!utcTime) return utcTime;
    const parts = utcTime.split(':');
    if (parts.length !== 2) return utcTime;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) return utcTime;
    const d = new Date();
    d.setUTCHours(h, m, 0, 0);
    return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0');
  }

  /** Convert local "HH:MM" string to UTC "HH:MM" string. */
  localTimeToUtc(localTime: string): string {
    if (!localTime) return localTime;
    const parts = localTime.split(':');
    if (parts.length !== 2) return localTime;
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) return localTime;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d.getUTCHours().toString().padStart(2, '0') + ':' + d.getUTCMinutes().toString().padStart(2, '0');
  }
}
